"use client"

import Link from "next/link"
import { useTranslations } from "next-intl"
import { Pizza, Coffee, Beef, Salad, Soup, Sandwich, Fish, Wine, Utensils } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { MARKETPLACE_CUISINE_TYPES } from "@/lib/marketplace/constants"
import type { MarketplaceCuisine } from "@/lib/marketplace/constants"

const CUISINE_ICONS: Record<MarketplaceCuisine, LucideIcon> = {
  hamburguesas: Sandwich,
  tacos: Utensils,
  pizza: Pizza,
  saludable: Salad,
  venezolano: Utensils,
  japones: Fish,
  cafe: Coffee,
  carnes: Beef,
  bar: Wine,
  asiatico: Soup,
  mediterraneo: Salad,
  otro: Utensils,
}

type Props = {
  selected: MarketplaceCuisine | null
  /** Href del chip: el padre construye la URL preservando el resto de filtros.
   *  Chips como <Link> = URLs de cocina crawleables (self-canonical cuando van
   *  solas), no botones que solo mutan estado. */
  hrefFor: (cuisine: MarketplaceCuisine | null) => string
}

export function CategoryFilter({ selected, hrefFor }: Props) {
  const t = useTranslations("marketplace")
  return (
    <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
      <Chip active={selected === null} href={hrefFor(null)} label={t("filters.all")} />
      {MARKETPLACE_CUISINE_TYPES.map((cuisine) => (
        <Chip
          key={cuisine}
          active={selected === cuisine}
          href={hrefFor(selected === cuisine ? null : cuisine)}
          label={t(`cuisines.${cuisine}`)}
          Icon={CUISINE_ICONS[cuisine]}
        />
      ))}
    </div>
  )
}

function Chip({
  active,
  href,
  label,
  Icon,
}: {
  active: boolean
  href: string
  label: string
  Icon?: LucideIcon
}) {
  return (
    <Link
      href={href}
      scroll={false}
      aria-current={active ? "page" : undefined}
      className={[
        "flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-semibold transition-all border",
        active
          ? "text-white border-transparent"
          : "text-[#7A7A7A] border-[#E8E4DF] bg-white hover:border-[#D7263D] hover:text-[#D7263D]",
      ].join(" ")}
      style={active ? { background: "linear-gradient(90deg,#D7263D,#F46B35)" } : undefined}
    >
      {Icon && <Icon className="w-[15px] h-[15px]" strokeWidth={2} aria-hidden />}
      {label}
    </Link>
  )
}
